import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import Copyright from '../components/Copyright';
import theme from '../components/theme';
import { emailRegex, pswRegex } from '../helpers/TextFields';
import { getAuth, signInWithEmailAndPassword, sendEmailVerification, browserSessionPersistence, browserLocalPersistence } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Hidden } from '@mui/material';

function Signin() {
  const navigate = useNavigate();
  const [errorTitle, setErrorTitle] = React.useState("Error");
  const [openErrorDialog, setOpenErrorDialog] = React.useState(false);
  const [openEmailSentDialog, setOpenEmailSentDialog] = React.useState(false);
  const [openPleaseValidateDialog, setOpenPleaseValidateDialog] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState('');
  const [circleProg, setCircleProg] = React.useState(false);


  const handleOpenEmailSentDialog = () => {
    setOpenPleaseValidateDialog(false);
    setOpenEmailSentDialog(true);
  };

  const handleCloseEmailSentDialog = () => {
    setOpenEmailSentDialog(false);
  };

  const handleOpenPleaseValidateDialog = () => {
    setOpenPleaseValidateDialog(true);
  };

  const handleClosePleaseValidateDialog = () => {
    setOpenPleaseValidateDialog(false);
  };

  const handleOpenErrorDialog = () => {
    setOpenErrorDialog(true);
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');
    if (!emailRegex.test(email)) {
      console.log("Invalid email.");
      setCircleProg(false);
    } else {
      setCircleProg(true);
      try {
        const auth = getAuth();
        signInWithEmailAndPassword(auth, email, password)
          .then((userCredential) => {
            setCircleProg(false);
            const session_user = userCredential.user;
            console.log("successful sign-in for user: ", session_user.uid);
            if (session_user.emailVerified) {
              navigate("/product");
              console.log("Remember session: ", remember);
              auth.setPersistence(remember ? browserLocalPersistence : browserSessionPersistence);
            } else {
              handleOpenPleaseValidateDialog();
              sendEmailVerification(session_user)
                .then(() => {
                  setCircleProg(false);
                  auth.signOut();
                  console.log("sent email... ", userCredential.user);
                  handleOpenEmailSentDialog();
                })
                .catch((error) => {
                  console.log(error);
                  setErrorCode(error.code); 
                  setErrorTitle("Verification email failed to send");
                  handleOpenErrorDialog();
                });
            }
          })
          .catch((error) => {
            setCircleProg(false);
            setErrorCode(error.code);
            const errorMessage = error.message;
            console.log("ERROR on sign-in... code: ", errorCode, "message: ", errorMessage);
            setErrorTitle("Sign in failed");
            handleOpenErrorDialog();
          });
      } catch (error) {
        console.error("Error during sign-in:", error);
        setCircleProg(false);
      }
    }
  };

      const handleEmailChange = (event) => {
        setEmail(event.target.value);
      };

      const handlePasswordChange = (event) => {
        setPassword(event.target.value);
      };

      return (
        <ThemeProvider theme={theme}>
          <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
              sx={{
                marginTop: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: "75vh",
              }}
            >
              <Avatar sx={{ m: 1, bgcolor: 'analogous.main' }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography component="h1" variant="h5">
                Sign in
              </Typography>
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={handleEmailChange}
                  error={!emailRegex.test(email)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handlePasswordChange}
                />
                <FormControlLabel
                  id="rememberControlLabel"
                  control={
                    <Checkbox
                      id="rememberCheckbox"
                      aria-labelledby="rememberControlLabel"
                      color="primary"
                      defaultValue={false}
                      value={remember}
                      onClick={(e) => { setRemember(e.target.checked); }}
                    />
                  }
                  label="Remember me"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Sign In
                </Button>
                <Grid container>
                  <Grid item xs>
                    <Link href="./forgotpsw" variant="body2">
                      Forgot password?
                    </Link>
                  </Grid>
                  <Grid item>
                    <Link href="./signup" variant="body2">
                      {"Don't have an account? Sign Up"}
                    </Link>
                  </Grid>
                </Grid>
              </Box>
              <Hidden xsUp={!circleProg}>
                <CircularProgress color="success" m={10} />
              </Hidden>
              <Dialog open={openErrorDialog} onClose={handleCloseErrorDialog}>
                <DialogTitle>{errorTitle}</DialogTitle>
                <DialogContent>
                  <Typography>
                    {errorCode}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseErrorDialog}>Close</Button>
                </DialogActions>
              </Dialog>
              <Dialog open={openPleaseValidateDialog} onClose={handleClosePleaseValidateDialog}>
                <DialogTitle>Email Verification Required</DialogTitle>
                <DialogContent>
                  <Typography>
                    To complete your registration, please verify your email address by clicking the link we sent to <b>{email}</b>. <br /><br />If you do not see the email in your inbox, please check your spam folder.
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={() => window.location.href='/signin'}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog open={openEmailSentDialog} onClose={handleCloseEmailSentDialog}>
                <DialogTitle>Email Verification Required</DialogTitle>
                <DialogContent>
                  <Typography>
                    To complete your registration, please verify your email address by clicking the new link we sent to <b>{email}</b>. <br /><br />If you do not see the email in your inbox, please check your spam folder.
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={() => window.location.href='/signin'}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
            <Copyright sx={{ mt: 8, mb: 4 }} />
          </Container>
        </ThemeProvider>
      );
    }

    export default Signin;